<div class="developers-page">

    <header class="topbar">
        <div class="topbar-inner">
            <button class="menu-toggle" (click)="toggleSidebar()" aria-label="Abrir menú de navegación">
                <mat-icon>menu</mat-icon>
            </button>
            <div class="brand" (click)="goToLogin()">
                <img class="brand-logo" src="/assets/img/brand/handly-logo-lime-white.svg" alt="Handly" />
                <span class="brand-sep">/</span>
                <span class="brand-title">Documentación</span>
            </div>
            <div class="topbar-actions">
                <span class="version-badge">API pública · v1</span>
                <button type="button" class="btn-back" (click)="goToLogin()">
                    <mat-icon class="btn-icon">arrow_back</mat-icon> Volver a Handly
                </button>
            </div>
        </div>
        <div class="scroll-progress" [style.width.%]="scrollProgress"></div>
    </header>

    <div class="body-layout">
        <div class="sidebar-backdrop" *ngIf="sidebarOpen" (click)="toggleSidebar()"></div>
        <nav class="docs-sidebar" [class.open]="sidebarOpen">
            <ng-container *ngFor="let group of navGroups">
                <div class="nav-group-label">{{ group.label }}</div>
                <a *ngFor="let s of group.items" [class.active]="activeSectionId === s.id" (click)="scrollTo(s.id)">
                    <mat-icon>{{ s.icon }}</mat-icon>
                    <span>{{ s.label }}</span>
                </a>
            </ng-container>
        </nav>

        <main class="content">
          <div class="content-inner">

            <div class="intro-banner">
                <h1>API Pública de Handly</h1>
                <p>Documentación de referencia para integradores y desarrolladores que consumen la API pública.</p>
            </div>

            <!-- INTRODUCCIÓN -->
            <section id="intro" #sectionEl>
                <h2><mat-icon>description</mat-icon> Introducción</h2>
                <p>
                    La API pública de Handly permite a integraciones externas (por ejemplo, plataformas de
                    reservas o gestión propia) consultar y crear información en la cuenta de un usuario:
                    actividades, contactos, impuestos, cuentas bancarias, cajas de efectivo y facturas.
                </p>
                <p>
                    Todos los endpoints (salvo el de autenticación) cuelgan de la base:
                </p>
                <app-code-block language="bash" [code]="'https://api.handly.es/public/v1/'"></app-code-block>
                <p>
                    Las peticiones y respuestas van en formato <strong>JSON</strong> (salvo la petición de
                    token, que es <code>application/x-www-form-urlencoded</code>, como exige el estándar
                    OAuth2). Todos los campos de las respuestas usan <strong>camelCase</strong>.
                </p>
            </section>

            <!-- AUTENTICACIÓN -->
            <section id="auth" #sectionEl>
                <h2><mat-icon>vpn_key</mat-icon> Autenticación</h2>
                <p>
                    La API usa OAuth2 <em>Resource Owner Password Credentials</em>. En vez de tu contraseña
                    real, se recomienda usar tu <strong>API Key</strong> como password. Así puedes rotarla o
                    revocarla sin afectar tu login normal en la aplicación.
                </p>

                <h3>1. Solicita tu API Key</h3>
                <p>
                    Escribe a <a href="mailto:soporte@handly.es">soporte&#64;handly.es</a> solicitando
                    acceso a la API pública. El equipo de soporte generará tu <strong>API Key</strong> y te la
                    hará llegar. Guárdala, la usarás en todas las llamadas futuras.
                </p>
                <p>
                    Una vez activa, también puedes consultarla tú mismo iniciando sesión con tu contraseña
                    real para obtener un token, y con ese token llamando a:
                </p>
                <span class="method-badge get">GET</span> <code>/public/v1/api-key</code>
                <app-code-block title="Respuesta" language="json" [code]="apiKeyResponse"></app-code-block>

                <h3>2. Conseguir un token de acceso</h3>
                <span class="method-badge post">POST</span> <code>/auth/token</code>
                <app-code-block title="cURL" language="bash" [code]="authCurl"></app-code-block>
                <app-code-block title="Respuesta" language="json" [code]="authResponse"></app-code-block>
                <p>
                    El <code>access_token</code> se envía en el header <code>Authorization</code> de todas las
                    peticiones siguientes:
                </p>
                <app-code-block language="bash" [code]="'Authorization: Bearer {access_token}'"></app-code-block>
            </section>

            <!-- FORMATOS DE FECHA -->
            <section id="dates" #sectionEl>
                <h2><mat-icon>event</mat-icon> Formatos de fecha aceptados</h2>
                <p>Los campos de fecha (<code>date</code>, <code>dueDate</code>,
                    <code>paymentMethod.datePaided</code>, <code>rectifiedInvoices[].invoiceDate</code>)
                    aceptan <strong>dos</strong> formatos, de forma estricta y sin ambigüedad:
                </p>
                <ul class="date-formats">
                    <li><code>yyyy-MM-dd</code>, ej. <code>2026-07-21</code> (ISO 8601)</li>
                    <li><code>dd-MM-yyyy</code>, ej. <code>21-07-2026</code> (formato europeo)</li>
                </ul>
                <p class="warning">
                    <mat-icon>warning</mat-icon>
                    <span><code>MM-dd-yyyy</code> (formato americano) <strong>no se soporta</strong>: se
                        rechaza explícitamente con <code>400</code> en vez de arriesgarse a interpretar mal
                        el día y el mes.</span>
                </p>
            </section>

            <!-- CONSULTAS GET -->
            <section id="queries" #sectionEl>
                <h2><mat-icon>search</mat-icon> Consultas (GET)</h2>
                <p>Todos requieren el header <code>Authorization: Bearer &#123;access_token&#125;</code>.</p>

                <div class="endpoint" *ngFor="let e of getEndpoints">
                    <h3><span class="method-badge get">GET</span> <code>{{ e.path }}</code></h3>
                    <p>{{ e.description }}</p>
                    <app-code-block title="Respuesta" language="json" [code]="e.example"></app-code-block>
                </div>
            </section>

            <!-- CREAR FACTURAS -->
            <section id="invoices" #sectionEl>
                <h2><mat-icon>receipt</mat-icon> Crear facturas</h2>
                <span class="method-badge post">POST</span> <code>/public/v1/invoices</code>
                <p>
                    Un único endpoint crea tres tipos de documento, según el campo
                    <code>invoiceTypeCode</code>:
                </p>
                <table class="spec-table">
                    <tr>
                        <th>Código</th>
                        <th>Tipo</th>
                        <th>Notas</th>
                    </tr>
                    <tr>
                        <td><code>F1</code></td>
                        <td>Factura normal</td>
                        <td>Requiere contacto, actividad, fechas e ítems.</td>
                    </tr>
                    <tr>
                        <td><code>F2</code></td>
                        <td>Ticket / factura simplificada</td>
                        <td>Mismo shape que F1; se guarda como ticket, no como factura.</td>
                    </tr>
                    <tr>
                        <td><code>R1</code></td>
                        <td>Factura rectificativa</td>
                        <td>Solo necesita <code>activityId</code> y <code>rectifiedInvoices[]</code>; no
                            lleva <code>items</code>/<code>contact</code>/<code>date</code>.</td>
                    </tr>
                </table>

                <h3>Ejemplo: factura normal (F1)</h3>
                <app-code-block title="Request" language="json" [code]="invoiceRequestExample"></app-code-block>
                <app-code-block title="Respuesta" language="json" [code]="invoiceResponseExample"></app-code-block>

                <h3>Contacto</h3>
                <p>
                    Se identifica con <strong>uno</strong> de estos tres campos: <code>contactId</code> (Uid
                    de un contacto ya existente), <code>contactVatNumber</code> (se busca/crea por NIF/CIF) o
                    un objeto <code>contact</code> completo. La respuesta siempre devuelve el
                    <code>contactId</code> usado. Guárdalo para reutilizarlo en la siguiente factura del
                    mismo cliente y evitar contactos duplicados.
                </p>

                <h3>Método de pago</h3>
                <table class="spec-table">
                    <tr>
                        <th><code>type</code></th>
                        <th>Significado</th>
                        <th><code>id</code> corresponde a</th>
                    </tr>
                    <tr>
                        <td><code>Cash</code></td>
                        <td>Efectivo</td>
                        <td>Id de una caja de <code>GET efectivo</code></td>
                    </tr>
                    <tr>
                        <td><code>Bank</code></td>
                        <td>Transferencia bancaria</td>
                        <td>Uid de <code>GET/POST bankaccounts</code></td>
                    </tr>
                    <tr>
                        <td><code>Card</code></td>
                        <td>Tarjeta</td>
                        <td>Uid de cuenta bancaria</td>
                    </tr>
                    <tr>
                        <td><code>Remesa</code></td>
                        <td>Cargo en cuenta</td>
                        <td>Uid de cuenta bancaria</td>
                    </tr>
                </table>

                <h3>Rectificar una factura (R1)</h3>
                <app-code-block title="Request" language="json" [code]="rectifyRequestExample"></app-code-block>
                <p>
                    Se puede rectificar más de una factura en la misma llamada, listándolas en
                    <code>rectifiedInvoices</code>. Si alguna <code>invoiceNumber</code> no existe, la
                    respuesta es <code>404 INVOICE_NOT_FOUND</code> con el detalle de cuáles no se
                    encontraron.
                </p>
            </section>

            <!-- CREAR CUENTAS BANCARIAS -->
            <section id="bankaccounts" #sectionEl>
                <h2><mat-icon>account_balance</mat-icon> Crear cuentas bancarias</h2>
                <span class="method-badge post">POST</span> <code>/public/v1/bankaccounts</code>
                <p>Da de alta una cuenta bancaria en la cuenta del usuario autenticado.</p>

                <app-code-block title="Request" language="json" [code]="bankAccountRequestExample">
                </app-code-block>
                <app-code-block title="Respuesta" language="json" [code]="bankAccountResponseExample">
                </app-code-block>

                <p>
                    El <code>iban</code> se valida por formato <strong>y</strong> por dígito de control
                    (checksum ISO 7064 / mod-97). Un IBAN con formato correcto pero checksum inválido se
                    rechaza igualmente con <code>400</code>.
                </p>
                <p>
                    El <code>bankAccountId</code> devuelto se puede usar después como
                    <code>paymentMethod.id</code> (con <code>type: "Bank"</code>) al crear una factura.
                </p>
            </section>

            <!-- CÓDIGOS DE ERROR -->
            <section id="errors" #sectionEl>
                <h2><mat-icon>warning</mat-icon> Códigos de error</h2>
                <table class="spec-table">
                    <tr>
                        <th>HTTP</th>
                        <th>errorCode</th>
                        <th>Cuándo ocurre</th>
                    </tr>
                    <tr>
                        <td><span class="status-badge s400">400</span></td>
                        <td><code>INVALID_REQUEST</code></td>
                        <td>Datos de la petición inválidos (validación de campos).</td>
                    </tr>
                    <tr>
                        <td><span class="status-badge s401">401</span></td>
                        <td>—</td>
                        <td>Token ausente, inválido o caducado.</td>
                    </tr>
                    <tr>
                        <td><span class="status-badge s404">404</span></td>
                        <td><code>ACTIVITY_NOT_FOUND</code></td>
                        <td>El <code>activityId</code> no existe o no pertenece al usuario.</td>
                    </tr>
                    <tr>
                        <td><span class="status-badge s404">404</span></td>
                        <td><code>INVOICE_NOT_FOUND</code></td>
                        <td>Al rectificar (R1), alguna factura de <code>rectifiedInvoices</code> no existe.
                        </td>
                    </tr>
                    <tr>
                        <td><span class="status-badge s409">409</span></td>
                        <td><code>IBAN_ALREADY_REGISTERED</code></td>
                        <td>El IBAN ya está registrado para ese usuario.</td>
                    </tr>
                    <tr>
                        <td><span class="status-badge s500">500</span></td>
                        <td><code>INTERNAL_SERVER_ERROR</code></td>
                        <td>Error interno inesperado al procesar la petición.</td>
                    </tr>
                </table>
                <p>Todas las respuestas de error siguen el mismo formato:</p>
                <app-code-block language="json" [code]="errorResponseExample"></app-code-block>
            </section>

            <!-- CHANGELOG -->
            <section id="changelog" #sectionEl>
                <h2><mat-icon>history</mat-icon> Historial de cambios</h2>
                <ul class="changelog">
                    <li>
                        <strong>2026-07-21</strong>: esta página sustituye a la documentación en PDF.
                        Añadido <code>POST /public/v1/bankaccounts</code>. Documentados los códigos de
                        error reales (400/401/404/409/500) y los formatos de fecha aceptados
                        (<code>yyyy-MM-dd</code> y <code>dd-MM-yyyy</code>).
                    </li>
                </ul>
                <p>
                    Esta documentación se mantiene junto al código que describe. Cualquier cambio en la API
                    pública debe reflejarse aquí, en el mismo cambio.
                </p>
            </section>

            <footer class="docs-footer">
                <p>¿Dudas o problemas de integración? Escríbenos a
                    <a href="mailto:soporte@handly.es">soporte&#64;handly.es</a>
                </p>
            </footer>

          </div>
        </main>
    </div>
</div>
